<script setup>
import TransportSummaryDetailsTable from '@/components/transport-summary-page/TransportSummaryDetailsTable.vue'
import RevenueSummaryTotalsTable from './RevenueSummaryTotalsTable.vue'

import { ref, defineProps, computed } from 'vue'
import { calculateInvoiceTotal, getItemPrice } from '@/utils/revenueHelpers.js'

const props = defineProps({
  revenueList: {
    type: Array,
    default: () => []
  },
  freightCode: {
    type: Function,
    required: true
  },
  loading: {
    type: Boolean,
    default: false
  }
})

const showDetails = ref(false)

const revenueListSorted = computed(() => {
  const dataCopy = JSON.parse(JSON.stringify(props.revenueList))

  dataCopy.sort((a, b) => {
    const nameA = a.destination?.name || ''
    const nameB = b.destination?.name || ''

    return nameA.localeCompare(nameB)
  })

  return dataCopy
})

const hasAttribute = (obj, key) =>
  Object.prototype.hasOwnProperty.call(obj, key)

const revenueSummaryRows = computed(() => {
  const pigs = props.revenueList.filter((docket) =>
    hasAttribute(docket?.job_description, 'total_pigs')
  )
  const cartons = props.revenueList.filter((docket) =>
    hasAttribute(docket?.job_description, 'total_cartons')
  )

  const pigRow = buildSummaryRow('Porker', pigs, 'total_pigs')
  const cartonRow = buildSummaryRow('Cartons', cartons, 'total_cartons')

  const porkerTypeRows = buildPorkerTypeSummaryRows(pigs)

  const summary = [
    pigRow,
    ...porkerTypeRows,
    cartonRow,
    {
      type: 'Total',
      weight: pigRow.weight + cartonRow.weight,
      quantity: pigRow.quantity + cartonRow.quantity,
      dockets: pigRow.dockets + cartonRow.dockets,
      invoiceTotal: pigRow.invoiceTotal + cartonRow.invoiceTotal
    }
  ]

  return summary
})

const buildSummaryRow = (type, rows, quantityKey) => {
  return {
    type,
    weight: rows.reduce(
      (total, row) => total + Number(row.job_description?.total_weight || 0),
      0
    ),
    quantity: rows.reduce(
      (total, row) => total + Number(row.job_description?.[quantityKey] || 0),
      0
    ),
    dockets: rows.length,
    invoiceTotal: rows.reduce((total, row) => {
      return total + calculateInvoiceTotal(row.loaded_items)
    }, 0)
  }
}

function buildPorkerTypeSummaryRows(porkerDockets) {
  const summaryMap = new Map()

  porkerDockets.forEach((docket) => {
    const loadedItems = docket.loaded_items || []

    loadedItems.forEach((item) => {
      const normalisedType = normaliseProductType(item.producttype)
      const productType = normalisedType === 'PORKER' ? 'Pigs' : normalisedType

      const weight = Number(item.weight || 0)
      const itemPrice = getItemPrice(item)

      if (!summaryMap.has(productType)) {
        summaryMap.set(productType, {
          type: formatProductTypeLabel(productType),
          productType,
          weight: 0,
          quantity: 0,
          dockets: null,
          invoiceTotal: 0,
          isSubRow: true
        })
      }

      const summary = summaryMap.get(productType)

      summary.weight += weight
      summary.quantity += 1
      summary.invoiceTotal += itemPrice
    })
  })

  return Array.from(summaryMap.values())
}

function normaliseProductType(type) {
  return String(type || 'UNKNOWN')
    .trim()
    .toUpperCase()
}

function formatProductTypeLabel(type) {
  return type.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase())
}
</script>

<template>
  <v-card class="mb-3" :loading="loading">
    <v-card-title class="d-flex align-center">
      <v-row no-gutters>
        <v-col cols="12" md="6" class="d-flex align-center">
          <h4 class="header-title mb-0 font-weight-bold text-light-blue">
            Revenue Summary
          </h4>

          <v-spacer />

          <v-btn
            x-small
            text
            color="primary"
            @click="showDetails = !showDetails"
          >
            {{ showDetails ? 'Hide Details' : 'Show Details' }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card-title>

    <v-card-text>
      <v-row no-gutters class="align-end">
        <v-col cols="12" md="6">
          <RevenueSummaryTotalsTable :summary="revenueSummaryRows" />
        </v-col>
      </v-row>

      <v-expand-transition>
        <div v-show="showDetails" class="mt-3">
          <TransportSummaryDetailsTable
            :items="revenueListSorted"
            :freight-code="freightCode"
            :price-mode="'revenue'"
          />
        </div>
      </v-expand-transition>
    </v-card-text>
  </v-card>
</template>
